/**
 * Shared admin-hub PIN state — the single source of truth for which admin /
 * reporting surfaces a persona has pinned. Extracted from TimesheetAdminHubPage
 * so the SAME pinned set can render in three places: the hub (★ toggle), the
 * sidebar ("My Shortcuts"), and the timesheet dashboard ("My Shortcuts" tiles).
 *
 * Persistence: per-persona localStorage key `ts-adminhub-pins:<persona>` (the key
 * the hub already used, so existing pins carry over). Role-aware defaults seed a
 * fresh persona; the moment they pin/unpin, their choice is stored and we never
 * re-seed. State lives in a tiny Zustand store so a pin in the hub updates the
 * sidebar + dashboard live in the same tab.
 */
import { useEffect } from 'react'
import { create } from 'zustand'
import { TIMESHEET_ADMIN_GROUPS, type TimesheetAdminItem } from './adminGroups'
import { filterAdminGroupsByRole } from './roleAccessConfig'
import { isAdminPathBuilt } from './featureGatedAdminItems'

// Role-aware default pins — what a fresh persona lands with BEFORE pinning
// anything, drawn from the per-role cadence analysis (the ~daily surfaces a role
// touches). Keys must match adminGroups item keys; each role's set lives within
// the buckets that role can see (ADMIN_GROUP_VISIBILITY).
export const DEFAULT_PINS: Record<string, string[]> = {
  Lead: ['ts-approvals', 'ts-anomaly-dashboard', 'ts-assign-work', 'ts-pto-requests', 'ts-attendance-flags'],
  Supervisor: ['ts-approvals', 'ts-anomaly-dashboard', 'ts-assign-work', 'ts-pto-requests', 'ts-attendance-flags'],
  Timekeeper: ['ts-approvals', 'ts-customer-invoices', 'ts-anomaly-dashboard', 'ts-pto-requests'],
  // Oversight roles also start with Cert Compliance (folded in from the old
  // "Compliance oversight" dashboard panel). Floor Activity isn't a pinnable
  // admin surface, so it stays in the sidebar's Team section.
  ComplianceOfficer: ['ts-compliance-landing', 'ts-cert-reminders', 'ts-anomaly-dashboard', 'ts-audit-log', 'ts-floor-check', 'ts-cert-compliance'],
  Auditor: ['ts-compliance-landing', 'ts-cert-reminders', 'ts-anomaly-dashboard', 'ts-audit-log', 'ts-floor-check', 'ts-cert-compliance'],
  // Admin roles start with Policy Library (folded in from the old "Set up &
  // manage" panel). The Admin Hub gateway lives on the sidebar, not as a pin.
  ConfigAdmin: ['ts-charge-codes', 'ts-policy-library', 'ts-settings', 'ts-integrations-dashboard'],
  GlobalAdmin: ['ts-approvals', 'ts-compliance-landing', 'ts-anomaly-dashboard', 'ts-charge-codes', 'ts-policy-library', 'ts-integrations-dashboard'],
  GlobalViewer: ['ts-approvals', 'ts-compliance-landing', 'ts-anomaly-dashboard', 'ts-charge-codes', 'ts-policy-library', 'ts-integrations-dashboard'],
}

export function defaultPinsForRole(role: string | null | undefined): string[] {
  if (!role) return DEFAULT_PINS.GlobalAdmin!
  return DEFAULT_PINS[role] ?? DEFAULT_PINS.GlobalAdmin!
}

const pinsKey = (persona: string): string => `ts-adminhub-pins:${persona}`

function readStoredPins(persona: string): string[] | null {
  try {
    const v = localStorage.getItem(pinsKey(persona))
    return v ? (JSON.parse(v) as string[]) : null
  } catch {
    return null
  }
}
function writeStoredPins(persona: string, pins: string[]): void {
  try {
    localStorage.setItem(pinsKey(persona), JSON.stringify(pins))
  } catch {
    /* storage disabled — personalization just won't persist */
  }
}

interface AdminPinsState {
  byPersona: Record<string, string[]>
  /** Load a persona's pins into the store (stored value, else seed defaults). Idempotent. */
  load: (persona: string, defaults: string[]) => void
  /** Toggle a key for a persona; persists + updates the store reactively. */
  toggle: (persona: string, key: string) => void
  /** Reset a persona's pins back to their role defaults (persists + reactive). */
  reset: (persona: string, defaults: string[]) => void
}

export const useAdminPinsStore = create<AdminPinsState>((set, get) => ({
  byPersona: {},
  load: (persona, defaults) => {
    if (get().byPersona[persona]) return // already loaded this session
    const stored = readStoredPins(persona)
    set((s) => ({ byPersona: { ...s.byPersona, [persona]: stored ?? defaults } }))
  },
  toggle: (persona, key) =>
    set((s) => {
      const cur = s.byPersona[persona] ?? []
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]
      writeStoredPins(persona, next)
      return { byPersona: { ...s.byPersona, [persona]: next } }
    }),
  reset: (persona, defaults) =>
    set((s) => {
      writeStoredPins(persona, defaults)
      return { byPersona: { ...s.byPersona, [persona]: [...defaults] } }
    }),
}))

/**
 * Hook: the pinned set for a persona+role, with default seeding handled. Returns
 * the pinned keys, a membership test, and a toggle. Used by the hub, sidebar, and
 * dashboard so all three reflect the same live set.
 */
export function useAdminPins(persona: string, role: string | null | undefined) {
  const defaults = defaultPinsForRole(role)
  const load = useAdminPinsStore((s) => s.load)
  const toggle = useAdminPinsStore((s) => s.toggle)
  const resetStore = useAdminPinsStore((s) => s.reset)
  const stored = useAdminPinsStore((s) => s.byPersona[persona])
  // Seed on first use for this persona (load() is idempotent).
  useEffect(() => {
    load(persona, defaults)
    // defaults is derived from role; persona+role are the real deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona, role, load])
  const pinned = stored ?? defaults
  return {
    pinned,
    isPinned: (k: string) => pinned.includes(k),
    toggle: (k: string) => toggle(persona, k),
    /** Reset this persona's pins back to their role defaults. */
    resetToDefaults: () => resetStore(persona, defaults),
    /** True when the current set already equals the role defaults (hide the reset CTA). */
    isAtDefaults: pinned.length === defaults.length && pinned.every((k) => defaults.includes(k)),
  }
}

/**
 * Resolve pinned keys to their current admin-surface items, filtered to the
 * surfaces this role can actually reach AND that are built into this deployment
 * (#67e) — so a pin a role can no longer see, or whose feature isn't built, is
 * silently dropped (e.g. a default Integrations Dashboard pin won't render in
 * My Shortcuts when `timesheet.mfg-integrations` is off). Order follows the
 * pinned-key order. Filtering at resolve (not in DEFAULT_PINS) keeps the stored
 * pin so it reappears if the feature is later built into a fork.
 */
export function resolvePinnedItems(
  keys: string[],
  role: string | null | undefined,
): TimesheetAdminItem[] {
  const groups = filterAdminGroupsByRole(TIMESHEET_ADMIN_GROUPS, role)
  const byKey = new Map<string, TimesheetAdminItem>()
  for (const g of groups) for (const it of g.items) byKey.set(it.key, it)
  return keys
    .map((k) => byKey.get(k))
    .filter((x): x is TimesheetAdminItem => Boolean(x))
    .filter((it) => isAdminPathBuilt(it.path))
}

/** Every admin surface this role can reach AND that is built in (flat), for the "add a shortcut" picker. */
export function allAdminItemsForRole(role: string | null | undefined): TimesheetAdminItem[] {
  const groups = filterAdminGroupsByRole(TIMESHEET_ADMIN_GROUPS, role)
  return groups.flatMap((g) => g.items).filter((it) => isAdminPathBuilt(it.path))
}
